import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Navigate } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import CategoryDropdown from "./components/CategoryDropdown";
import CategoryGrid from "./components/CategoryGrid";
import ArticleCards from "./components/ArticleCards";
import { MAIN_SITE_URL } from "@/mocks/categories";
import { resolveLang } from "@/i18n/lang";
import { supportedLangs, defaultLang } from "@/lib/articles";
import { websiteSchema } from "@/lib/seo";

const heroImage =
  "https://readdy.ai/api/search-image?query=A%20fresh%20artistic%20abstract%20background%20of%20soft%20sage%20green%20cream%20and%20terracotta%20with%20flowing%20organic%20shapes%20and%20subtle%20grain%20texture%2C%20airy%20minimal%20composition%2C%20calm%20european%20editorial%20aesthetic%2C%20soft%20light%2C%20no%20text%2C%20no%20people&width=1600&height=900&seq=usfans-hero-01&orientation=landscape&nocache=true";

export default function Home() {
  const { lang } = useParams();
  const resolvedLang = resolveLang(lang);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (i18n.language !== resolvedLang) {
      i18n.changeLanguage(resolvedLang);
    }
  }, [resolvedLang, i18n]);

  useEffect(() => {
    document.documentElement.lang = resolvedLang;
  }, [resolvedLang]);

  // Redirect if unsupported lang (must be after all hooks)
  const isValidLang = lang && supportedLangs.includes(lang);
  if (!isValidLang) {
    return <Navigate to={`/${defaultLang}`} replace />;
  }

  const steps = [
    t("how.step1"),
    t("how.step2"),
    t("how.step3"),
    t("how.step4"),
    t("how.step5"),
    t("how.step6"),
  ];

  const popular = [
    t("popular.1"),
    t("popular.2"),
    t("popular.3"),
    t("popular.4"),
    t("popular.5"),
    t("popular.6"),
    t("popular.7"),
    t("popular.8"),
    t("popular.9"),
  ];

  const valueCards = [
    { image: "https://readdy.ai/api/search-image?query=A%20minimal%20flat%20lay%20of%20assorted%20fashion%20products%20a%20sneaker%20a%20folded%20hoodie%20and%20a%20tote%20bag%20arranged%20on%20a%20soft%20cream%20background%2C%20soft%20natural%20light%2C%20editorial%20product%20photography%2C%20clean%20european%20aesthetic&width=640&height=640&seq=usfans-value-01&orientation=squarish", title: t("value.card1.title"), desc: t("value.card1.desc") },
    { image: "https://readdy.ai/api/search-image?query=A%20minimal%20arrangement%20of%20a%20small%20leather%20wallet%20coins%20and%20a%20discount%20price%20tag%20on%20a%20soft%20sage%20green%20background%2C%20soft%20natural%20light%2C%20editorial%20product%20photography%2C%20clean%20minimal%20aesthetic&width=640&height=640&seq=usfans-value-02&orientation=squarish", title: t("value.card2.title"), desc: t("value.card2.desc") },
    { image: "https://readdy.ai/api/search-image?query=A%20neat%20arrangement%20of%20folded%20clothes%20sorted%20by%20category%20and%20color%20into%20tidy%20stacks%20on%20a%20soft%20peach%20background%2C%20soft%20natural%20light%2C%20editorial%20product%20photography%2C%20clean%20minimal%20aesthetic&width=640&height=640&seq=usfans-value-03&orientation=squarish", title: t("value.card3.title"), desc: t("value.card3.desc") },
    { image: "https://readdy.ai/api/search-image?query=An%20open%20notebook%20with%20a%20handwritten%20shopping%20checklist%20and%20a%20pen%20next%20to%20a%20small%20folded%20t-shirt%20on%20a%20soft%20oatmeal%20background%2C%20soft%20natural%20light%2C%20editorial%20product%20photography%2C%20clean%20minimal%20aesthetic&width=640&height=640&seq=usfans-value-04&orientation=squarish", title: t("value.card4.title"), desc: t("value.card4.desc") },
    { image: "https://readdy.ai/api/search-image?query=A%20fresh%20arrangement%20of%20a%20brand%20new%20pair%20of%20sneakers%20and%20a%20cream%20cap%20with%20a%20small%20gift%20box%20on%20a%20soft%20terracotta%20background%2C%20soft%20natural%20light%2C%20editorial%20product%20photography%2C%20clean%20minimal%20aesthetic&width=640&height=640&seq=usfans-value-05&orientation=squarish", title: t("value.card5.title"), desc: t("value.card5.desc") },
    { image: "https://readdy.ai/api/search-image?query=A%20simple%20starter%20set%20of%20a%20plain%20white%20t-shirt%20sneakers%20and%20a%20canvas%20tote%20bag%20on%20a%20soft%20blush%20background%2C%20soft%20natural%20light%2C%20editorial%20product%20photography%2C%20clean%20minimal%20aesthetic&width=640&height=640&seq=usfans-value-06&orientation=squarish", title: t("value.card6.title"), desc: t("value.card6.desc") },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={websiteSchema(resolvedLang)} />
      <Header lang={resolvedLang} />

      <main>
        {/* Hero */}
        <section className="relative min-h-[560px] md:min-h-[640px] flex items-center">
          <div className="absolute inset-0">
            <img src={heroImage} alt="usfans spreadsheet resource background" className="w-full h-full object-cover object-top" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          <div className="relative z-10 w-full px-4 md:px-6 pt-24 pb-16">
            <div className="mx-auto max-w-4xl text-center text-background-50">
              <p className="inline-block text-xs md:text-sm font-semibold tracking-widest uppercase mb-5 text-background-100/90">
                {t("hero.eyebrow")}
              </p>
              <h1 className="font-heading font-extrabold text-3xl md:text-5xl leading-tight mb-5">
                {t("hero.title")}
              </h1>
              <p className="text-base md:text-lg text-background-100/90 max-w-2xl mx-auto mb-8 leading-relaxed">
                {t("hero.subtitle")}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 text-sm md:text-base font-semibold whitespace-nowrap transition-colors w-full sm:w-auto"
                >
                  {t("hero.ctaPrimary")}
                </a>
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center rounded-md bg-background-50 hover:bg-background-100 text-foreground-900 px-6 py-3 text-sm md:text-base font-semibold whitespace-nowrap transition-colors w-full sm:w-auto"
                >
                  {t("hero.ctaSecondary")}
                </a>
              </div>
              <p className="mt-6 text-xs md:text-sm text-background-100/80">{t("hero.trust")}</p>
            </div>
          </div>
        </section>

        {/* Value proposition */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mb-3">
                {t("value.title")}
              </h2>
              <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed">{t("value.subtitle")}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {valueCards.map((card) => (
                <div key={card.title} className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
                  <div className="w-full h-40 md:h-44 overflow-hidden">
                    <img src={card.image} alt={card.title} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading font-semibold text-foreground-900 mb-2">{card.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{card.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Spreadsheet section */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mb-4">
                {t("spreadsheet.title")}
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-6">{t("spreadsheet.copy")}</p>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-3 text-sm font-semibold whitespace-nowrap transition-colors"
              >
                {t("spreadsheet.cta")}
              </a>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://readdy.ai/api/search-image?query=A%20minimal%20flat%20lay%20of%20folded%20neutral%20toned%20clothing%20sneakers%20and%20accessories%20on%20a%20soft%20terracotta%20background%2C%20soft%20natural%20light%2C%20editorial%20product%20photography%2C%20clean%20european%20aesthetic&width=640&height=640&seq=usfans-side-01&orientation=squarish&nocache=true"
                alt="usfans spreadsheet products"
                className="w-full h-40 md:h-52 object-cover object-top rounded-lg"
              />
              <img
                src="https://readdy.ai/api/search-image?query=A%20minimal%20arrangement%20of%20a%20cream%20hoodie%20and%20a%20tan%20leather%20bag%20with%20soft%20accessories%20on%20a%20soft%20peach%20background%2C%20soft%20natural%20light%2C%20editorial%20product%20photography%2C%20clean%20european%20aesthetic&width=640&height=640&seq=usfans-side-02&orientation=squarish&nocache=true"
                alt="usfans spreadsheet fashion finds"
                className="w-full h-40 md:h-52 object-cover object-top rounded-lg"
              />
              <img
                src="https://readdy.ai/api/search-image?query=A%20minimal%20flat%20lay%20of%20folded%20t-shirts%20in%20sage%20green%20white%20and%20sand%20on%20a%20soft%20oatmeal%20background%2C%20soft%20natural%20light%2C%20editorial%20product%20photography%2C%20clean%20european%20aesthetic&width=640&height=640&seq=usfans-side-03&orientation=squarish&nocache=true"
                alt="usfans spreadsheet t-shirts"
                className="w-full h-40 md:h-52 object-cover object-top rounded-lg"
              />
              <img
                src="https://readdy.ai/api/search-image?query=A%20minimal%20arrangement%20of%20white%20sneakers%20and%20a%20sage%20green%20bucket%20hat%20on%20a%20soft%20cream%20background%2C%20soft%20natural%20light%2C%20editorial%20product%20photography%2C%20clean%20european%20aesthetic&width=640&height=640&seq=usfans-side-04&orientation=squarish&nocache=true"
                alt="usfans spreadsheet shoes and headwear"
                className="w-full h-40 md:h-52 object-cover object-top rounded-lg"
              />
            </div>
          </div>
        </section>

        {/* Categories */}
        <section id="categories" className="w-full px-4 md:px-6 py-14 md:py-20 scroll-mt-20">
          <div className="mx-auto max-w-7xl">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mb-3">
                {t("categories.title")}
              </h2>
              <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed mb-8">
                {t("categories.subtitle")}
              </p>
              <div className="flex justify-center">
                <CategoryDropdown />
              </div>
            </div>
            <CategoryGrid />
          </div>
        </section>

        {/* How it works */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mb-3">{t("how.title")}</h2>
              <p className="text-foreground-600">{t("how.subtitle")}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {steps.map((step, i) => (
                <div key={step} className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                  <div className="w-9 h-9 shrink-0 flex items-center justify-center rounded-full bg-accent-500 text-background-50 font-bold text-sm">
                    {i + 1}
                  </div>
                  <p className="text-sm font-medium text-foreground-800 pt-1.5">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Popular searches */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="mx-auto max-w-7xl">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mb-3">{t("popular.title")}</h2>
              <p className="text-foreground-600">{t("popular.subtitle")}</p>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {popular.map((term) => (
                <a
                  key={term}
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center rounded-full bg-secondary-100 text-secondary-900 px-4 py-2 text-sm font-medium hover:bg-secondary-200 transition-colors whitespace-nowrap"
                >
                  {term}
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Safety */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-accent-50">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mb-4">{t("safety.title")}</h2>
            <p className="text-foreground-600 leading-relaxed mb-6 max-w-2xl mx-auto">{t("safety.copy")}</p>
            <a
              href="#categories"
              className="inline-flex items-center rounded-md bg-accent-600 hover:bg-accent-700 text-background-50 px-5 py-3 text-sm font-semibold whitespace-nowrap transition-colors"
            >
              {t("safety.cta")}
            </a>
          </div>
        </section>

        {/* Articles */}
        <section id="articles" className="w-full px-4 md:px-6 py-14 md:py-20 scroll-mt-20">
          <div className="mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mb-3">{t("articles.title")}</h2>
              <p className="text-foreground-600">{t("articles.subtitle")}</p>
            </div>
            <ArticleCards lang={resolvedLang} />
          </div>
        </section>

        {/* Final CTA */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-primary-500">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-background-50 mb-4">{t("final.title")}</h2>
            <p className="text-background-100/90 leading-relaxed mb-6">{t("final.copy")}</p>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center rounded-md bg-background-50 hover:bg-background-100 text-foreground-900 px-6 py-3 text-sm md:text-base font-semibold whitespace-nowrap transition-colors"
            >
              {t("final.cta")}
            </a>
          </div>
        </section>
      </main>

      <Footer lang={resolvedLang} />
    </div>
  );
}